import { Box, Heading, Text } from '@chakra-ui/react'
import type { IImage } from '@stocker/ui-components/design-system'
import { NextLink } from '@stocker/ui-components/design-system'
import { formatHeadlineColor } from '@stocker/ui-components/helpers'
import NextImage from 'next/image'
import type React from 'react'
import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { HighlightFirstWord } from '../../elements/HighlightFirstWord/HighlightFirstWord'

export interface INewsCard {
  image: IImage
  headline: string
  text: ReactNode
  link: {
    absolutePath: string
  }
  highlightedFirstWord?: string
}
interface IColor {
  colorText?: string
  colorAccent?: string
}
interface ICombined extends INewsCard, IColor {}

export const FeaturedNewsCard: React.FC<ICombined> = ({
  image,
  headline,
  highlightedFirstWord,
  text,
  link,
  colorText = 'primaryText.500',
  colorAccent = 'accent.500',
}) => {
  const intl = useIntl()
  return (
    <Box
      w="100%"
      h="100%"
      display="flex"
      flexDirection="column"
      transition="transform 0.3s ease, box-shadow 0.3s ease"
      _hover={{
        transform: 'scale(1.02)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
      }}
    >
      <NextLink href={`/news/${link.absolutePath}`}>
        <Box
          position="relative"
          overflow="hidden"
          cursor="pointer"
        >
          <NextImage
            src={image.fullpath ?? '/'}
            width="450"
            height="300"
            placeholder={image.blurDataUrl ? 'blur' : 'empty'}
            blurDataURL={image.blurDataUrl}
            sizes="(max-width: 768px) 100vw, 33vw"
            style={{
              width: '100%',
              height: 'auto',
            }}
            alt={headline}
          />
        </Box>
      </NextLink>
      <Box bg="black" p="20px" flex="1">
        <NextLink href={`/news/${link.absolutePath}`}>
          <Heading
            as="h4"
            lineHeight="1.4"
            fontSize={{ base: 'md', md: 'lg' }}
            fontWeight={700}
            fontFamily="primary"
            cursor="pointer"
            color="white"
            _hover={{
              color: colorAccent,
            }}
          >
            <HighlightFirstWord productLine={highlightedFirstWord ?? ''} />
            {formatHeadlineColor(headline)}
          </Heading>
        </NextLink>
        <Box
          fontSize="sm"
          lineHeight="1.6"
          color="gray.400"
          mt="12px"
          noOfLines={4}
        >
          {text}
        </Box>
        <NextLink href={`/news/${link.absolutePath}`}>
          <Text
            fontSize="md"
            fontFamily="primary"
            fontWeight={700}
            cursor="pointer"
            mt="12px"
            color="white"
            _hover={{
              color: colorAccent,
            }}
          >
            {intl.formatMessage({ id: '--more' })}
          </Text>
        </NextLink>
      </Box>
    </Box>
  )
}
